import { useState, useEffect, type FormEvent } from 'react'
import {
  Upload, CheckCircle, AlertCircle, Loader, Puzzle,
  Layers, Briefcase, XCircle, ChevronDown, Target, FileInput,
  Wrench, FileOutput, ClipboardCheck, Lightbulb, Award, BookOpen,
  FileText, Scale, Shield,
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import { apiGet, apiPost } from '../services/api'
import type { Competency, Tool, Task, ApiResponse } from '../engine/types'

const difficultyOptions = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8']

const roleOptions = ['teacher', 'expert', 'student', 'practitioner'] as const
type ContributorRole = (typeof roleOptions)[number]

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  draft:      { bg: 'rgba(90,94,120,0.12)', text: 'var(--color-text-muted)', label: 'Draft' },
  reviewing:  { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)', label: 'Reviewing' },
  approved:   { bg: 'rgba(96,165,250,0.12)', text: 'var(--color-info)', label: 'Approved' },
  published:  { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)', label: 'Published' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)', label: 'Deprecated' },
}

interface ContributorInfo {
  name: string
  role: ContributorRole | ''
  organization: string
}

interface ContributeForm {
  title: string
  competency_id: string
  difficulty: string
  tools_allowed: string[]
  expected_steps: number
  description: string
  contributor: ContributorInfo
  // Section 4: Expert Reference Answer
  reference_answer: string
  key_decision_points: string
  common_mistakes: string
  // Section 5: Evaluation Criteria
  scoring_dimensions: string
  unacceptable_answers: string
  // Section 6: Data & Licensing
  input_files_desc: string
  output_format: string
  license: string
  allow_training_use: boolean
  attribution_name: string
}

const emptyForm: ContributeForm = {
  title: '',
  competency_id: '',
  difficulty: '',
  tools_allowed: [],
  expected_steps: 40,
  description: '',
  contributor: { name: '', role: '', organization: '' },
  reference_answer: '',
  key_decision_points: '',
  common_mistakes: '',
  scoring_dimensions: '',
  unacceptable_answers: '',
  input_files_desc: '',
  output_format: '',
  license: '',
  allow_training_use: false,
  attribution_name: '',
}

const inputStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-primary)',
}

const badExampleStyle: React.CSSProperties = {
  backgroundColor: 'rgba(248,113,113,0.08)',
  border: '1px solid rgba(248,113,113,0.2)',
}

const goodExampleStyle: React.CSSProperties = {
  backgroundColor: 'rgba(74,222,128,0.08)',
  border: '1px solid rgba(74,222,128,0.2)',
}

function focusAccent(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
  e.currentTarget.style.borderColor = 'var(--color-accent)'
}
function blurBorder(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
  e.currentTarget.style.borderColor = 'var(--color-border)'
}

// ---- Demo task data ----
interface DemoTask {
  id: string
  titleKey: { zh: string; en: string }
  inputKey: { zh: string; en: string }
  softwareKey: { zh: string; en: string }
  outputKey: { zh: string; en: string }
  evaluationKey: { zh: string; en: string }
}

const demoTasks: DemoTask[] = [
  {
    id: 'demo-1',
    titleKey: { zh: '旅游规划 — 杭州3日亲子游', en: 'Travel Planning — 3-Day Hangzhou Family Trip' },
    inputKey: { zh: '用户画像(2大1小)、预算¥5000、日期、出发城市', en: 'User profile (2 adults, 1 child), budget ¥5000, dates, departure city' },
    softwareKey: { zh: 'Browser, Search, Maps, Calculator, Spreadsheet', en: 'Browser, Search, Maps, Calculator, Spreadsheet' },
    outputKey: { zh: '完整行程方案(含交通、住宿、景点、预算)', en: 'Complete itinerary (transport, lodging, attractions, budget)' },
    evaluationKey: { zh: '预算≤¥5000、必含西湖、亲子友好、路线合理', en: 'Budget ≤¥5000, must include West Lake, family-friendly, reasonable route' },
  },
  {
    id: 'demo-2',
    titleKey: { zh: '商业决策 — 新能源市场进入策略', en: 'Business Decision — EV Market Entry Strategy' },
    inputKey: { zh: '行业背景、预算¥5000万、时间范围', en: 'Industry context, budget ¥50M, timeframe' },
    softwareKey: { zh: 'Browser, Search, Calculator, Spreadsheet', en: 'Browser, Search, Calculator, Spreadsheet' },
    outputKey: { zh: '市场分析报告+竞争策略+ROI计算', en: 'Market analysis report + competitive strategy + ROI calculation' },
    evaluationKey: { zh: '含SWOT分析、3+竞争对手分析、风险矩阵、ROI数据', en: 'Includes SWOT, 3+ competitor analyses, risk matrix, ROI data' },
  },
  {
    id: 'demo-3',
    titleKey: { zh: '财务规划 — 家庭综合财务规划', en: 'Financial Planning — Comprehensive Family Finance' },
    inputKey: { zh: '家庭收入、资产状况、目标', en: 'Family income, assets, goals' },
    softwareKey: { zh: 'Browser, Search, Calculator, Spreadsheet', en: 'Browser, Search, Calculator, Spreadsheet' },
    outputKey: { zh: '预算分配+投资组合+保险配置', en: 'Budget allocation + investment portfolio + insurance config' },
    evaluationKey: { zh: '教育基金目标、退休金目标、风险保障', en: 'Education fund goal, retirement goal, risk coverage' },
  },
]

// ---- Criteria data ----
interface Criterion {
  icon: typeof Layers
  titleKey: 'contribute.complex' | 'contribute.representative' | 'contribute.verifiable'
  descKey: 'contribute.complexDesc' | 'contribute.representativeDesc' | 'contribute.verifiableDesc'
  badKey: { zh: string; en: string }
  goodKey: { zh: string; en: string }
}

const criteria: Criterion[] = [
  {
    icon: Layers,
    titleKey: 'contribute.complex',
    descKey: 'contribute.complexDesc',
    badKey: {
      zh: '在Excel中应用一个筛选 (单一操作，<10步)',
      en: 'Apply a single filter in Excel (single op, <10 steps)',
    },
    goodKey: {
      zh: '为1000人企业制定年度旅游规划方案 (多约束、多步骤、需深度分析)',
      en: 'Plan annual travel for 1000-person company (multi-constraint, multi-step, deep analysis)',
    },
  },
  {
    icon: Briefcase,
    titleKey: 'contribute.representative',
    descKey: 'contribute.representativeDesc',
    badKey: {
      zh: '用文本编辑器写旅游攻略 (非专业工具)',
      en: 'Write travel guide in text editor (non-professional tool)',
    },
    goodKey: {
      zh: '使用搜索引擎+地图+计算器+电子表格完成旅游规划 (专业工具组合)',
      en: 'Use search+maps+calculator+spreadsheet for travel planning (pro tool combo)',
    },
  },
  {
    icon: CheckCircle,
    titleKey: 'contribute.verifiable',
    descKey: 'contribute.verifiableDesc',
    badKey: {
      zh: '设计一个完美的旅游路线 (无限可能，难以比较)',
      en: 'Design a perfect travel route (infinite possibilities, hard to compare)',
    },
    goodKey: {
      zh: '在¥3000预算内规划杭州3日游，必含西湖 (约束明确，可验证)',
      en: 'Plan 3-day Hangzhou trip under ¥3000, must include West Lake (clear constraints, verifiable)',
    },
  },
]

// ---- Submission structure steps ----
interface StructureStep {
  num: number
  emoji: string
  titleKey: 'contribute.step1' | 'contribute.step2' | 'contribute.step3' | 'contribute.step4' | 'contribute.step5'
  descKey: 'contribute.step1Desc' | 'contribute.step2Desc' | 'contribute.step3Desc' | 'contribute.step4Desc' | 'contribute.step5Desc'
}

const structureSteps: StructureStep[] = [
  { num: 1, emoji: '🎯', titleKey: 'contribute.step1', descKey: 'contribute.step1Desc' },
  { num: 2, emoji: '📥', titleKey: 'contribute.step2', descKey: 'contribute.step2Desc' },
  { num: 3, emoji: '🛠️', titleKey: 'contribute.step3', descKey: 'contribute.step3Desc' },
  { num: 4, emoji: '📤', titleKey: 'contribute.step4', descKey: 'contribute.step4Desc' },
  { num: 5, emoji: '✅', titleKey: 'contribute.step5', descKey: 'contribute.step5Desc' },
]

export default function ContributeTaskPage() {
  const { t, lang } = useLang()
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [tools, setTools] = useState<Tool[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [form, setForm] = useState<ContributeForm>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successId, setSuccessId] = useState<string | null>(null)
  const [recentTasks, setRecentTasks] = useState<Task[]>([])
  const [expandedDemo, setExpandedDemo] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function fetchAll() {
      try {
        setLoading(true)
        setLoadError(null)
        const [compRes, toolRes, taskRes] = await Promise.all([
          apiGet<ApiResponse<Competency[]>>('/competencies'),
          apiGet<ApiResponse<Tool[]>>('/tools'),
          apiGet<ApiResponse<Task[]>>('/tasks'),
        ])
        if (cancelled) return
        setCompetencies(compRes.data ?? [])
        setTools(toolRes.data ?? [])
        // Show most recent first
        const sorted = [...(taskRes.data ?? [])].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        setRecentTasks(sorted.slice(0, 5))
      } catch (err) {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchAll()
    return () => { cancelled = true }
  }, [])

  function updateForm<K extends keyof ContributeForm>(key: K, value: ContributeForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  function toggleTool(toolId: string) {
    setForm(prev => ({
      ...prev,
      tools_allowed: prev.tools_allowed.includes(toolId)
        ? prev.tools_allowed.filter(id => id !== toolId)
        : [...prev.tools_allowed, toolId],
    }))
  }

  function toggleDemo(id: string) {
    setExpandedDemo(prev => (prev === id ? null : id))
  }

  function validate(): boolean {
    if (!form.title.trim() || !form.competency_id || !form.description.trim()) {
      setSubmitError(t('contribute.error'))
      return false
    }
    return true
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setSuccessId(null)
    if (!validate()) return

    try {
      setSubmitting(true)
      const payload: Partial<Task> & {
        contributor?: ContributorInfo
        reference_answer?: string
        key_decision_points?: string
        common_mistakes?: string
        scoring_dimensions?: string
        unacceptable_answers?: string
        input_files_desc?: string
        output_format?: string
        license?: string
        allow_training_use?: boolean
        attribution_name?: string
      } = {
        title: form.title.trim(),
        competency_id: form.competency_id,
        difficulty: form.difficulty,
        tools_allowed: form.tools_allowed,
        expected_steps: form.expected_steps,
        description: form.description.trim(),
        status: 'reviewing',
        contributor: form.contributor,
        reference_answer: form.reference_answer.trim(),
        key_decision_points: form.key_decision_points.trim(),
        common_mistakes: form.common_mistakes.trim(),
        scoring_dimensions: form.scoring_dimensions.trim(),
        unacceptable_answers: form.unacceptable_answers.trim(),
        input_files_desc: form.input_files_desc.trim(),
        output_format: form.output_format.trim(),
        license: form.license,
        allow_training_use: form.allow_training_use,
        attribution_name: form.attribution_name.trim(),
      }
      const created = await apiPost<ApiResponse<Task>>('/tasks', payload)
      setSuccessId(created.data?.id ?? '')
      setForm(emptyForm)
      // Refresh recent submissions
      try {
        const refreshed = await apiGet<ApiResponse<Task[]>>('/tasks')
        const sorted = [...(refreshed.data ?? [])].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        setRecentTasks(sorted.slice(0, 5))
      } catch {
        // ignore refresh error
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('contribute.error'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div
          className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-xl"
          style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
        >
          <AlertCircle size={32} style={{ color: 'var(--color-danger)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.error')}</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  const benefits = [
    { icon: Lightbulb, key: 'contribute.benefit1' as const },
    { icon: BookOpen, key: 'contribute.benefit2' as const },
    { icon: Award, key: 'contribute.benefit3' as const },
  ]

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* ============ 1. Hero Section ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-accent-glow)' }}
          >
            <Upload size={22} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {t('contribute.title')}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              {t('contribute.subtitle')}
            </p>
          </div>
        </div>

        {/* Benefits Banner */}
        <div
          className="rounded-xl border p-5"
          style={{
            backgroundColor: 'rgba(108,140,255,0.06)',
            borderColor: 'rgba(108,140,255,0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Award size={16} style={{ color: 'var(--color-accent)' }} />
            <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {t('contribute.benefits')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {benefits.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="flex items-start gap-3 p-3 rounded-lg"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
              >
                <Icon size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
                <span className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {t(key)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 2. Task Criteria Section ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-5">
          <Target size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('contribute.criteria')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {criteria.map(({ icon: Icon, titleKey, descKey, badKey, goodKey }) => (
            <div
              key={titleKey}
              className="rounded-xl border p-5 flex flex-col gap-4"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              {/* Icon + Title + Desc */}
              <div>
                <div className="flex items-center gap-2.5 mb-2">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-accent-glow)' }}
                  >
                    <Icon size={18} style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {t(titleKey)}
                  </h3>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {t(descKey)}
                </p>
              </div>

              {/* Bad Example */}
              <div className="rounded-lg p-3" style={badExampleStyle}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <XCircle size={13} style={{ color: 'var(--color-danger)' }} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-danger)' }}>
                    {t('contribute.badExample')}
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {badKey[lang]}
                </p>
              </div>

              {/* Good Example */}
              <div className="rounded-lg p-3" style={goodExampleStyle}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CheckCircle size={13} style={{ color: 'var(--color-success)' }} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-success)' }}>
                    {t('contribute.goodExample')}
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {goodKey[lang]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 3. Submission Structure ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-5">
          <ClipboardCheck size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('contribute.structure')}
          </h2>
        </div>
        <div
          className="rounded-xl border p-5"
          style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {structureSteps.map((step, idx) => (
              <div key={step.num} className="relative">
                <div className="flex flex-col items-center text-center gap-2">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shrink-0"
                    style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                  >
                    {step.num}
                  </div>
                  <div className="text-xl">{step.emoji}</div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                    {t(step.descKey)}
                  </p>
                </div>
                {/* Connector arrow (hidden on small screens / last item) */}
                {idx < structureSteps.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-5 -right-2.5 text-lg"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4. Demo Tasks ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-5">
          <Lightbulb size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('contribute.demoTasks')}
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {demoTasks.map(demo => {
            const expanded = expandedDemo === demo.id
            return (
              <div
                key={demo.id}
                className="rounded-xl border overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
              >
                {/* Card Header (clickable) */}
                <button
                  type="button"
                  onClick={() => toggleDemo(demo.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-[var(--color-bg-tertiary)]"
                  style={{ backgroundColor: expanded ? 'var(--color-bg-tertiary)' : 'transparent' }}
                  aria-expanded={expanded}
                >
                  <span className="text-sm font-semibold leading-snug" style={{ color: 'var(--color-text-primary)' }}>
                    {demo.titleKey[lang]}
                  </span>
                  <ChevronDown
                    size={16}
                    className="shrink-0 transition-transform duration-200"
                    style={{
                      color: 'var(--color-text-secondary)',
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>

                {/* Expandable Details */}
                {expanded && (
                  <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="pt-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileInput size={12} style={{ color: 'var(--color-info)' }} />
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                          {t('contribute.step2')}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {demo.inputKey[lang]}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Wrench size={12} style={{ color: 'var(--color-info)' }} />
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                          {t('contribute.step3')}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {demo.softwareKey[lang]}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileOutput size={12} style={{ color: 'var(--color-info)' }} />
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                          {t('contribute.step4')}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {demo.outputKey[lang]}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <ClipboardCheck size={12} style={{ color: 'var(--color-success)' }} />
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                          {t('contribute.step5')}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {demo.evaluationKey[lang]}
                      </p>
                    </div>
                  </div>
                )}

                {/* View Details toggle hint (when collapsed) */}
                {!expanded && (
                  <div
                    className="px-4 py-2 border-t text-center text-[11px]"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                  >
                    {t('contribute.viewDetails')}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* ============ 5. Submission Form ============ */}
      <section>
        <div className="flex items-center gap-2 mb-5">
          <Upload size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('contribute.submit')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {/* ---- Section 1: Task Description ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  1
                </div>
                <div className="flex items-center gap-2">
                  <Target size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {t('contribute.step1')}
                  </h3>
                </div>
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('contribute.taskTitle')} *
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={e => updateForm('title', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={inputStyle}
                />
              </div>

              {/* Competency + Difficulty */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {t('contribute.competency')} *
                  </label>
                  <select
                    required
                    value={form.competency_id}
                    onChange={e => updateForm('competency_id', e.target.value)}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer transition-colors"
                    style={inputStyle}
                  >
                    <option value="">{t('contribute.selectCompetency')}</option>
                    {competencies.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.name_cn})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {t('contribute.difficulty')} *
                  </label>
                  <select
                    required
                    value={form.difficulty}
                    onChange={e => updateForm('difficulty', e.target.value)}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer transition-colors"
                    style={inputStyle}
                  >
                    <option value="">{t('contribute.selectDifficulty')}</option>
                    {difficultyOptions.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Task Description */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('contribute.description')} *
                </label>
                <textarea
                  required
                  rows={6}
                  value={form.description}
                  onChange={e => updateForm('description', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={t('contribute.descriptionPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ---- Section 2: Input & Tools ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  2
                </div>
                <div className="flex items-center gap-2">
                  <Wrench size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {lang === 'zh' ? '输入与工具' : 'Input & Tools'}
                  </h3>
                </div>
              </div>

              {/* Allowed Tools */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('contribute.tools')}
                </label>
                {tools.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>—</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {tools.map(tool => {
                      const checked = form.tools_allowed.includes(tool.id)
                      return (
                        <label
                          key={tool.id}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                          style={{
                            backgroundColor: checked ? 'var(--color-accent-glow)' : 'var(--color-bg-tertiary)',
                            border: `1px solid ${checked ? 'var(--color-accent)' : 'var(--color-border)'}`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTool(tool.id)}
                            className="h-3.5 w-3.5 accent-current"
                            style={{ accentColor: 'var(--color-accent)' }}
                          />
                          <span className="text-xs truncate" style={{ color: 'var(--color-text-primary)' }}>
                            {tool.name}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Expected Steps */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('contribute.expectedSteps')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.expected_steps}
                  onChange={e => updateForm('expected_steps', Math.max(1, parseInt(e.target.value) || 1))}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ---- Section 3: Contributor Info ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  3
                </div>
                <div className="flex items-center gap-2">
                  <Puzzle size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {t('contribute.contributor')}
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>
                    {t('contribute.name')}
                  </label>
                  <input
                    type="text"
                    value={form.contributor.name}
                    onChange={e => updateForm('contributor', { ...form.contributor, name: e.target.value })}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>
                    {t('contribute.role')}
                  </label>
                  <select
                    value={form.contributor.role}
                    onChange={e => updateForm('contributor', { ...form.contributor, role: e.target.value as ContributorRole | '' })}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer transition-colors"
                    style={inputStyle}
                  >
                    <option value="">—</option>
                    {roleOptions.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>
                    {t('contribute.organization')}
                  </label>
                  <input
                    type="text"
                    value={form.contributor.organization}
                    onChange={e => updateForm('contributor', { ...form.contributor, organization: e.target.value })}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* ---- Section 4: Expert Reference Answer ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  4
                </div>
                <div className="flex items-center gap-2">
                  <FileText size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {lang === 'zh' ? '专家参考答案' : 'Expert Reference Answer'}
                  </h3>
                </div>
              </div>

              {/* Reference Answer */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '参考答案或推荐方案' : 'Reference answer or recommended solution'}
                </label>
                <textarea
                  rows={8}
                  value={form.reference_answer}
                  onChange={e => updateForm('reference_answer', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '请提供您作为专家的参考答案或推荐方案' : 'Provide your expert reference answer or recommended solution'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>

              {/* Key Decision Points */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '关键决策点及推荐选择' : 'Key decision points and recommended choices'}
                </label>
                <textarea
                  rows={4}
                  value={form.key_decision_points}
                  onChange={e => updateForm('key_decision_points', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '例如：交通方式：高铁>自驾；住宿选择：西湖附近>市中心' : 'e.g., Transport: HSR>driving; Lodging: near West Lake>downtown'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>

              {/* Common Mistakes */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '常见错误决策和陷阱' : 'Common mistakes and pitfalls'}
                </label>
                <textarea
                  rows={4}
                  value={form.common_mistakes}
                  onChange={e => updateForm('common_mistakes', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '例如：忽略天气因素、预算超支、景点安排过密' : 'e.g., Ignoring weather, budget overruns, over-packed attractions'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ---- Section 5: Evaluation Criteria ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  5
                </div>
                <div className="flex items-center gap-2">
                  <Scale size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {lang === 'zh' ? '评分标准' : 'Evaluation Criteria'}
                  </h3>
                </div>
              </div>

              {/* Scoring Dimensions */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '建议评分维度及权重' : 'Suggested scoring dimensions and weights'}
                </label>
                <textarea
                  rows={3}
                  value={form.scoring_dimensions}
                  onChange={e => updateForm('scoring_dimensions', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '例如：准确性40%、过程30%、效率20%、约束10%' : 'e.g., Accuracy 40%, Process 30%, Efficiency 20%, Constraints 10%'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>

              {/* Unacceptable Answers */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '不可接受的答案特征' : 'Unacceptable answer characteristics'}
                </label>
                <textarea
                  rows={3}
                  value={form.unacceptable_answers}
                  onChange={e => updateForm('unacceptable_answers', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '例如：预算超支、遗漏必去景点、未考虑天气风险' : 'e.g., Budget overruns, missing must-visit spots, ignoring weather risks'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none transition-colors"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ---- Section 6: Data & Licensing ---- */}
            <div
              className="rounded-xl border p-6 space-y-5"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-3 pb-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                >
                  6
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={15} style={{ color: 'var(--color-accent)' }} />
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {lang === 'zh' ? '数据与授权' : 'Data & Licensing'}
                  </h3>
                </div>
              </div>

              {/* Input Files Desc + Output Format */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {lang === 'zh' ? '必要输入文件描述' : 'Required input files description'}
                  </label>
                  <input
                    type="text"
                    value={form.input_files_desc}
                    onChange={e => updateForm('input_files_desc', e.target.value)}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    placeholder={lang === 'zh' ? '例如：客户需求文档、预算表' : 'e.g., Customer requirement doc, budget sheet'}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {lang === 'zh' ? '预期输出格式' : 'Expected output format'}
                  </label>
                  <input
                    type="text"
                    value={form.output_format}
                    onChange={e => updateForm('output_format', e.target.value)}
                    onFocus={focusAccent}
                    onBlur={blurBorder}
                    placeholder={lang === 'zh' ? '例如：结构化行程方案+预算明细' : 'e.g., Structured itinerary + budget breakdown'}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* License */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '授权选择' : 'License choice'}
                </label>
                <select
                  value={form.license}
                  onChange={e => updateForm('license', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer transition-colors"
                  style={inputStyle}
                >
                  <option value="">{lang === 'zh' ? '请选择...' : 'Select...'}</option>
                  <option value="CC-BY-4.0">{lang === 'zh' ? 'CC-BY 4.0 署名（推荐）' : 'CC-BY 4.0 Attribution (Recommended)'}</option>
                  <option value="CC-BY-SA-4.0">{lang === 'zh' ? 'CC-BY-SA 4.0 署名-相同方式共享' : 'CC-BY-SA 4.0 Share-Alike'}</option>
                  <option value="CC-BY-NC-4.0">{lang === 'zh' ? 'CC-BY-NC 4.0 署名-非商业' : 'CC-BY-NC 4.0 Non-Commercial'}</option>
                  <option value="proprietary">{lang === 'zh' ? '保留所有权利' : 'All Rights Reserved'}</option>
                </select>
              </div>

              {/* Allow Training Use (checkbox) */}
              <label
                className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                style={{
                  backgroundColor: form.allow_training_use ? 'var(--color-accent-glow)' : 'var(--color-bg-tertiary)',
                  border: `1px solid ${form.allow_training_use ? 'var(--color-accent)' : 'var(--color-border)'}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.allow_training_use}
                  onChange={e => updateForm('allow_training_use', e.target.checked)}
                  className="h-3.5 w-3.5"
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                <span className="text-xs" style={{ color: 'var(--color-text-primary)' }}>
                  {lang === 'zh' ? '允许用于模型训练' : 'Allow use for model training'}
                </span>
              </label>

              {/* Attribution Name */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {lang === 'zh' ? '署名名称（如需署名）' : 'Attribution name (if you want credit)'}
                </label>
                <input
                  type="text"
                  value={form.attribution_name}
                  onChange={e => updateForm('attribution_name', e.target.value)}
                  onFocus={focusAccent}
                  onBlur={blurBorder}
                  placeholder={lang === 'zh' ? '例如：张三 / Z. San' : 'e.g., Jane Doe'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Success / Error Messages */}
            {successId && (
              <div
                className="flex items-center gap-2 text-sm p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(74,222,128,0.12)', color: 'var(--color-success)' }}
              >
                <CheckCircle size={15} />
                <span>
                  {t('contribute.success')} <code className="font-mono font-semibold">{successId}</code>
                </span>
              </div>
            )}
            {submitError && (
              <div
                className="flex items-center gap-2 text-sm p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: 'var(--color-danger)' }}
              >
                <AlertCircle size={15} />
                {submitError}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
              >
                {submitting ? (
                  <Loader size={15} className="animate-spin" />
                ) : (
                  <Upload size={15} />
                )}
                {submitting ? t('contribute.submitting') : t('contribute.submit')}
              </button>
            </div>
          </form>

          {/* Recent Submissions Sidebar */}
          <div className="lg:col-span-1">
            <div
              className="rounded-xl border p-5 sticky top-4"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Puzzle size={16} style={{ color: 'var(--color-accent)' }} />
                <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {t('contribute.recentSubmissions')}
                </h2>
              </div>
              {recentTasks.length === 0 ? (
                <div className="text-center py-8">
                  <Puzzle size={28} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
                  <p className="mt-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {t('contribute.noSubmissions')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {recentTasks.map(task => {
                    const status = statusConfig[task.status] ?? statusConfig.draft
                    return (
                      <li
                        key={task.id}
                        className="rounded-lg p-3"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <h3 className="text-sm font-medium leading-snug line-clamp-2" style={{ color: 'var(--color-text-primary)' }}>
                            {task.title}
                          </h3>
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0"
                            style={{ backgroundColor: status.bg, color: status.text }}
                          >
                            {status.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-mono" style={{ color: 'var(--color-text-muted)' }}>
                            {task.difficulty}
                          </span>
                          <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                            {new Date(task.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
